<template>
	<div
		class="radio"
		:disabled="disabled"
		:class="[disabled && 'is-disabled']"
	>
		<input
			class="radio__input"
			:name="name"
			type="radio"
			:value="radioValue"
			@change="onClick"
			:id="uid + value"
			:checked="checked"
			:aria-label="ariaLabel"
		/>
		<div class="radio__body" :style="radioStyle">
			<Label
				class="radio__label"
				:forElementId="uid + value"
				:labelText="labelText"
			>
				<template v-slot:leading>
					<slot name="beforeLabel"> </slot>
				</template>

				<template v-slot:trailing>
					<slot name="afterLabel"> </slot>
				</template>
			</Label>
			<HelpFeedback
				class="radio__help-feedback"
				:helpText="helpText"
				v-if="helpText"
			/>
		</div>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';

export default {
	name: 'Radio',
	mixins: [baseComponentMixin],
	components: {
		Label,
		HelpFeedback
	},
	data() {
		return {
			boundValue: this.radioValue
		};
	},
	props: {
		value: {
			type: [String, Number, Boolean],
			default: ''
		},
		radioValue: {
			type: [String, Number, Boolean],
			default: ''
		},
		labelText: {
			type: String,
			default: null
		},
		name: {
			type: String,
			required: true
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		helpText: {
			type: String,
			default: null
		},
		width: {
			type: [String, Number],
			default: null
		}
	},
	methods: {
		onClick() {
			this.$emit('input', this.radioValue);
			this.$emit('change', this.radioValue);
		}
	},
	computed: {
		checked() {
			return this.value === this.radioValue;
		},
		radioStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.radio {
	display: inline-flex;
	align-items: flex-start;
	color: var(--field-fg);

	&__input {
		position: relative;
		cursor: pointer;
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
		outline: none;
		vertical-align: middle;
		width: 16px;
		min-width: 16px;
		height: 16px;
		border-radius: 50%;
		border: $field-border;
		background-color: var(--field-bg);

		&:checked {
			&::after {
				content: '';
				@include transform-full-center;
				@include create-circle(8px, 8px, var(--field-fg));
			}
		}

		&:hover {
			border: 2px solid var(--brand-2);
		}

		&:focus {
			&:before {
				position: absolute;
				content: '';
				@include transform-full-center;
				width: calc(100% + 10px);
				height: calc(100% + 10px);
				border-radius: 50%;
				border: $focus-width $focus-style var(--brand-2);
			}

			&:hover {
				&:before {
					width: calc(100% + 12px);
					height: calc(100% + 12px);
				}
			}
		}
	}

	&__label {
		display: flex;
		font-size: $field-font-size;
		cursor: pointer;
	}

	&__body {
		margin-left: $gutter-md;
	}

	&:deep(.label) {
		font-weight: $regular-font-weight;
	}
}
</style>
